import { useCallback, useEffect, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import DateRangeSelector from '../../../components/DateRangeSelector';
import { PAGE_CARD_SX } from '../../../theme/pageCardSx';
import FilterSelectorUser, {
  type UserFilterValue,
} from '../../../components/pointhome/roulette/chart/FilterSelectorUser';
import DeadClickTable, {
  type DeadClickDailyRow,
} from '../../../components/pointhome/experiment/DeadClickTable';
import apiClient from '../../../utils/axiosClient';

interface DeadClickEventStat {
  eventLabel: string;
  dailyStats: DeadClickDailyRow[];
}

interface DeadClickReportResponse {
  statsByEvent: DeadClickEventStat[];
}

function mapUserFilterToUserType(filter: UserFilterValue): string {
  if (filter === '기존 사용자') return 'remained';
  if (filter === '신규 사용자') return 'new';
  return 'all';
}

const DEAD_CLICK_TABLE_COUNT = 3;

/** 알려진 실험 영역 eventLabel 표시 순서 (정렬용). 여기 없는 라벨은 정렬 시 뒤로 밀림. */
const DEAD_CLICK_EVENT_ORDER = [
  '룰렛 메인화면 해달 영역',
  '룰렛 메인화면 어망 영역',
  '룰렛 메인화면 미션 완료 영역',
];

// S3 관리 이미지 -> CDN URL로 직접 렌더링
const CDN_BASE = process.env.REACT_APP_CDN_BASE_URL ?? '';
const ANALYTICS_IMAGE_PATH = process.env.REACT_APP_ANALYTICS_IMAGE_PATH ?? '/images/analytics';
const DEAD_CLICK_IMAGE_BASE = `${CDN_BASE}${ANALYTICS_IMAGE_PATH}`;

function sortStatsByEventOrder(stats: DeadClickEventStat[]): DeadClickEventStat[] {
  const orderFor = (label: string) => {
    const i = DEAD_CLICK_EVENT_ORDER.indexOf(label);
    return i === -1 ? 999 : i;
  };
  return [...stats].sort((a, b) => orderFor(a.eventLabel) - orderFor(b.eventLabel));
}

/** 매핑되는 영역만 URL 반환. 알 수 없는 eventLabel은 이미지 미표시(undefined). */
function getDeadClickImageUrl(eventLabel: string): string | undefined {
  const label = eventLabel ?? '';

  if (label.includes('해달')) {
    return `${DEAD_CLICK_IMAGE_BASE}/dead-1_decoded.png`;
  }

  if (label.includes('어망')) {
    return `${DEAD_CLICK_IMAGE_BASE}/dead-2_decoded.png`;
  }

  if (label.includes('미션 완료')) {
    return `${DEAD_CLICK_IMAGE_BASE}/dead-3_decoded.png`;
  }

  return undefined;
}

/** DeadClickTable imageWidth — 미션 완료 영역만 가로를 좁게 */
function getDeadClickImageWidth(eventLabel: string): number {
  return eventLabel.includes('미션 완료') ? 260 : 320;
}

export default function DeadClickLab() {
  const [startDate, setStartDate] = useState<Dayjs | null>(dayjs().subtract(6, 'day'));
  const [endDate, setEndDate] = useState<Dayjs | null>(dayjs());
  const [selectedApp, setSelectedApp] = useState<string | null>('앱 전체');
  const [appOptions, setAppOptions] = useState<string[]>(['앱 전체']);
  const [userFilter, setUserFilter] = useState<UserFilterValue>('전체');
  const [deadClickReport, setDeadClickReport] = useState<DeadClickReportResponse | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchFilterOptions = useCallback(() => {
    apiClient
      .get(`/api/v1/stat/pointhome/app/list`)
      .then((response) => {
        setAppOptions(['앱 전체', ...(response.data || [])]);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  const fetchDeadClickReport = useCallback(() => {
    if (!startDate || !endDate) return;
    const start = startDate.format('YYYY-MM-DD');
    const end = endDate.format('YYYY-MM-DD');
    const app = selectedApp === '앱 전체' ? 'all' : selectedApp ?? 'all';
    const user_type = mapUserFilterToUserType(userFilter);
    setLoading(true);
    apiClient
      .get<DeadClickReportResponse>('/api/v1/analytics/pointhome/roulette/dead-click', {
        params: { start_date: start, end_date: end, app, user_type },
      })
      .then((res) => setDeadClickReport(res.data))
      .catch(() => setDeadClickReport(null))
      .finally(() => setLoading(false));
  }, [startDate, endDate, selectedApp, userFilter]);

  useEffect(() => {
    fetchDeadClickReport();
  }, [fetchDeadClickReport]);

  const rawStats = deadClickReport?.statsByEvent ?? [];
  // 카드는 최대 3개만 노출. API가 3개를 넘기면 정렬 후 앞 3개만 보이며 나머지는 UI에 안 나감 — 이벤트 추가 시 DEAD_CLICK_TABLE_COUNT·DEAD_CLICK_EVENT_ORDER 검토
  const tablesToShow = sortStatsByEventOrder(rawStats).slice(0, DEAD_CLICK_TABLE_COUNT);

  return (
    <Box sx={{ opacity: loading ? 0.3 : 1, transition: 'opacity 0.3s ease', p: { xs: 2, md: 3 } }}>
      {/* 상단 필터: 기간, 앱, 사용자 */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'stretch',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          mb: 3,
        }}
      >
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 260 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <DateRangeSelector
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
        </Box>
        <Box
          sx={{
            flex: { xs: 'none', sm: '0 1 auto' },
            minWidth: { xs: '100%', sm: 160 },
            maxWidth: { xs: '100%', sm: 'none' },
          }}
        >
          <FilterSelectorUser value={userFilter} onChange={setUserFilter} />
        </Box>
      </Box>

      {/* 앱 리스트 + 데드클릭 테이블  */}
      <Box
        sx={{
          ...PAGE_CARD_SX,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        {/* 페이지 타이틀 */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'flex-start', md: 'center' },
            justifyContent: 'space-between',
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: '#0f172a', mb: 0.5 }}
            >
              Dead-Click 실험실
            </Typography>
          </Box>
        </Box>

        {/* 실험 시작일 안내 배너 */}
        <Box
          sx={{
            borderRadius: 2,
            border: '1px solid #bfdbfe',
            bgcolor: '#eff6ff',
            px: 2.5,
            py: 1.75,
            mb: 3,
          }}
        >
          <Typography
            variant="body2"
            sx={{ color: '#1d4ed8', fontWeight: 500 }}
          >
            실험 시작일은 룰렛 개편 최초 오픈일인 <strong>2026.02.11</strong> 이며,
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: '#1d4ed8', fontWeight: 500 }}
          >
            실험 영역 1은 예외로 <strong>2026.03.20</strong>에 시작되었습니다.
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            overflowX: 'auto',
            pb: 2,
            '&::-webkit-scrollbar': { height: 8 },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: '#d1d5db', borderRadius: 4 },
            '&::-webkit-scrollbar-thumb:hover': { backgroundColor: '#9ca3af' },
          }}
        >
          {appOptions.map((name) => (
            <Chip
              key={name}
              label={name}
              onClick={() => setSelectedApp(name)}
              sx={{
                cursor: 'pointer',
                fontWeight: selectedApp === name ? 700 : 400,
                backgroundColor: selectedApp === name ? '#0044D2' : '#fff',
                color: selectedApp === name ? '#fff' : '#374151',
                border: `1px solid ${selectedApp === name ? '#0044D2' : '#e5e7eb'}`,
                '&:hover': {
                  backgroundColor: selectedApp === name ? '#0033a0' : '#f3f4f6',
                },
                height: 36,
                fontSize: '0.875rem',
              }}
            />
          ))}
        </Stack>

        {/* 데드클릭 테이블 */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            flexDirection: 'row',
            gap: 2,
            flexWrap: 'wrap',
          }}
        >
          {loading ? (
            <Box sx={{ flex: '1 1 100%', display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : tablesToShow.length === 0 ? (
            <Box
              sx={{
                flex: '1 1 100%',
                py: 4,
                textAlign: 'center',
                color: '#6b7280',
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: '#fafafa',
              }}
            >
              <Typography variant="body2">데드클릭 데이터가 없습니다.</Typography>
            </Box>
          ) : (
            tablesToShow.map((eventStat, index) => {
              const imageSrc = getDeadClickImageUrl(eventStat.eventLabel);
              return (
              <Box
                key={eventStat.eventLabel}
                sx={{ flex: '1 1 0', minWidth: 280, maxWidth: '33.33%' }}
              >
                <DeadClickTable
                  experimentLabel={`실험 영역 ${index + 1}`}
                  title={eventStat.eventLabel}
                  imageSrc={imageSrc}
                  imageWidth={getDeadClickImageWidth(eventStat.eventLabel)}
                  rows={eventStat.dailyStats}
                />
              </Box>
              );
            })
          )}
        </Box>
      </Box>
    </Box>
  );
}
